import React from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/common';
import CustomerForm from '../CustomerForm';
import { CreateCustomerRequest, UpdateCustomerRequest } from '@/types/customer';
import { useUIStore } from '@/store/uiStore';
import { toast } from '@/store/toastStore';
import { customerApi } from '@/api/endpoints/customers';

const CreateCustomerModal: React.FC = () => {
  const { t } = useTranslation();
  const { modals, closeModal, refreshCallbacks } = useUIStore();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (data: CreateCustomerRequest | UpdateCustomerRequest, driverId?: string) => {
    setLoading(true);
    try {
      const createdCustomer = await customerApi.createCustomer(data as CreateCustomerRequest);
      
      // Assign driver if provided
      if (driverId && createdCustomer.code) {
        try {
          await customerApi.assignDriver(createdCustomer.code, driverId);
        } catch (driverError) {
          console.error('Error assigning driver:', driverError);
          // Don't fail the whole operation if driver assignment fails
          toast.warning(t('common.warning'), 'Customer created but driver assignment failed');
        }
      }
      
      toast.success(
        t('common.success'),
        t('customers.messages.createSuccess')
      );
      
      // Trigger refresh in parent component
      refreshCallbacks.customers?.();
      
      closeModal('createCustomer');
      // Trigger refresh of customer list
      window.dispatchEvent(new CustomEvent('refreshCustomers'));
    } catch (error: any) {
      console.error('Error creating customer:', error);
      const errorMessage = error.message || t('common.error');
      toast.error(t('common.error'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndClose = async (data: CreateCustomerRequest | UpdateCustomerRequest, driverId?: string) => {
    setLoading(true);
    try {
      const createdCustomer = await customerApi.createCustomer(data as CreateCustomerRequest);
      
      // Assign driver if provided
      if (driverId && createdCustomer.code) {
        try {
          await customerApi.assignDriver(createdCustomer.code, driverId);
        } catch (driverError) {
          console.error('Error assigning driver:', driverError);
          toast.warning(t('common.warning'), 'Customer created but driver assignment failed');
        }
      }
      
      toast.success(
        t('common.success'),
        t('customers.messages.createSuccess')
      );
      closeModal('createCustomer');
      // Trigger refresh of customer list
      window.dispatchEvent(new CustomEvent('refreshCustomers'));
    } catch (error: any) {
      console.error('Error creating customer:', error);
      const errorMessage = error.message || t('common.error');
      toast.error(t('common.error'), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    closeModal('createCustomer');
  };

  return (
    <Modal
      isOpen={modals.createCustomer}
      onClose={handleCancel}
      title={t('customers.create')}
      size="xl"
    >
      <CustomerForm
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onSaveAndClose={handleSaveAndClose}
        loading={loading}
      />
    </Modal>
  );
};

export default CreateCustomerModal; 